import React, { useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import { formatDuration } from "../utils/formatDuration";

function getMethod(eventName) {
  const upper = (eventName || "").toUpperCase();
  if (upper.startsWith("GET ")) return "GET";
  if (upper.startsWith("POST ")) return "POST";
  if (upper.startsWith("PUT ")) return "PUT";
  if (upper.startsWith("DELETE ")) return "DELETE";
  if (upper.startsWith("PATCH ")) return "PATCH";
  return null;
}

function stripQueryFromEventName(eventName) {
  if (!eventName) return eventName;
  const qIndex = eventName.indexOf("?");
  return qIndex >= 0 ? eventName.slice(0, qIndex) : eventName;
}

export default function EventsFeed({ requests = [] }) {
  const [hideParams, setHideParams] = useState(true);

  const { aggregated, totalMs, totalEvents } = useMemo(() => {
    if (!requests || requests.length === 0)
      return { aggregated: [], totalMs: 0, totalEvents: 0 };

    const map = new Map();
    for (const req of requests) {
      const rawName = req.eventName || "(unknown)";
      const key = hideParams ? stripQueryFromEventName(rawName) : rawName;
      if (!map.has(key))
        map.set(key, { eventName: key, count: 0, totalDurationMs: 0 });
      const entry = map.get(key);
      entry.count += req.count || 1;
      entry.totalDurationMs += req.totalDurationMs || 0;
    }

    const rows = Array.from(map.values())
      .map((e) => ({
        ...e,
        avgMs: e.count > 0 ? e.totalDurationMs / e.count : 0,
      }))
      .sort((a, b) => b.totalDurationMs - a.totalDurationMs);

    return {
      aggregated: rows,
      totalMs: rows.reduce((sum, r) => sum + r.totalDurationMs, 0),
      totalEvents: rows.reduce((sum, r) => sum + r.count, 0),
    };
  }, [requests, hideParams]);

  if (!requests || requests.length === 0) {
    return <p className="text-muted">No API requests recorded for this period.</p>;
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "0.5rem",
        }}
      >
        <Form.Check
          type="switch"
          id="events-feed-hide-params"
          label="Hide query parameters"
          checked={hideParams}
          onChange={(e) => setHideParams(e.target.checked)}
        />
      </div>
      <div className="data-table-wrapper">
        <table className="table table-sm table-hover mb-0">
          <thead className="table-light">
            <tr>
              <th>Event</th>
              <th
                style={{
                  textAlign: "right",
                  whiteSpace: "nowrap",
                  paddingRight: "2rem",
                }}
              >
                Avg Duration
              </th>
              <th>% of Total Duration</th>
            </tr>
          </thead>
          <tbody>
            {aggregated.map((row) => {
              const method = getMethod(row.eventName);
              const label = method
                ? row.eventName.slice(method.length + 1)
                : row.eventName;
              const pct =
                totalMs > 0
                  ? Math.round((row.totalDurationMs / totalMs) * 100)
                  : 0;
              return (
                <tr key={row.eventName}>
                  <td>
                    {method && (
                      <span
                        className={`request-method request-method--${method.toLowerCase()}`}
                        style={{ marginRight: "0.4rem" }}
                      >
                        {method}
                      </span>
                    )}
                    {label}
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      whiteSpace: "nowrap",
                      paddingRight: "2rem",
                    }}
                  >
                    {formatDuration(row.avgMs)}
                  </td>
                  <td style={{ minWidth: 140 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          height: 6,
                          background: "#e9ecef",
                          borderRadius: 3,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: `${pct}%`,
                            background: "#015cab",
                            borderRadius: 3,
                          }}
                        />
                      </div>
                      <span
                        style={{
                          fontSize: "0.75rem",
                          color: "#6c757d",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {pct}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="data-pagination mt-2">
        <span>
          {totalEvents} event{totalEvents !== 1 ? "s" : ""}
          &nbsp;·&nbsp;{aggregated.length} unique event
          {aggregated.length !== 1 ? "s" : ""}
        </span>
      </div>
    </div>
  );
}
